import React, { memo, useCallback, useRef, useState } from 'react';
import * as Yup from 'yup';
import { UploadOutlined } from '@ant-design/icons';
import { Col, Input, InputRef, Row, Typography } from 'antd';

import { INestedItem } from 'store/slices/openingInfo/interfaces';
import { IDocument } from 'types/document';

import { CustomUpload } from '../CustomUpload/CustomUpload';
import { CustomCollapse } from './components/CustomCollapse/CustomCollapse';
import styles from './Form.module.less';

interface IProps {
  onDelete: (id: string) => void;
  onMove: (entity: INestedItem, step?: number) => void;
  onUpdate: (file: IDocument, title: string, entity: INestedItem) => void;
  entity: INestedItem;
  total: number;
}

export const AdditionalForm = memo(
  ({ onUpdate, onDelete, onMove, total, entity }: IProps) => {
    const inputRef = useRef<InputRef>(null);
    const [title, setTitle] = useState<string>(entity.title);
    const [file, setFile] = useState<IDocument>(entity.file);
    const [error, setError] = useState<string>(null);

    const handleBlur = () => !error && onUpdate(file, title, entity);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const isValid = Yup.string().required().isValidSync(value);
      if (!isValid) {
        setError('Введите от 1 до 255 символов');
      } else {
        setError(null);
      }
      setTitle(e.target.value);
    };

    const handleChangeState = useCallback((file: IDocument) => {
      const title = file.realName.split('.').slice(0, -1).join('.');
      setTitle(title);
      setFile(file);
      inputRef.current.focus();
    }, []);

    return (
      <CustomCollapse
        onMove={step => onMove(entity, step)}
        onDelete={onDelete}
        title={title}
        file={file}
        itemId={entity.id}
        total={total}
        sortIndex={entity.sortIndex}
        isError={!!error}
      >
        <Row gutter={[24, 24]}>
          <Col flex={1}>
            <Input
              ref={inputRef}
              className={styles.input}
              placeholder='Название документа'
              type='text'
              value={title}
              onChange={handleChange}
              onBlur={handleBlur}
              minLength={1}
              maxLength={255}
              status={error ? 'error' : undefined}
              addonBefore={<span className={styles.required} />}
            />
            {error && <Typography.Text type='danger'>{error}</Typography.Text>}
          </Col>
          <Col>
            <CustomUpload
              onAdd={handleChangeState}
              icon={<UploadOutlined />}
              buttonTitle='Загрузить другой'
            />
          </Col>
        </Row>
      </CustomCollapse>
    );
  }
);

AdditionalForm.displayName = 'AdditionalForm';
